import * as React from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ArchiveRestore } from "lucide-react";
import { Text } from "../../../ui/components/Text";
import { Callout } from "../../../ui/components/Callout";
import { ListView } from "../../../ui/components/ListView";
import { ListViewCard } from "../../../ui/components/ListView/components/ListViewCard";
import { Avatar } from "../../../ui/components/Avatar";
import type { ColumnDefinition } from "../../../ui/components/Table/types";
import type { ListParams, ListResult } from "../../../ui/api/listApi.types";
import { DetailPageLayout } from "../layouts/DetailPageLayout";
import { useAuth } from "../../lib/auth/hooks";
import { getAuthCookie } from "../../lib/auth/session-cookie";
import { useI18n } from "../../lib/i18n";
import { certificationProgressClaimsQueryOptions } from "../../lib/api/certification-queries";
import {
  fetchCertificationModules,
  previewProgressClaimMigration,
  type ProgressClaim,
} from "../../lib/api/certifications";
import { vars } from "../../../ui/styles/theme.css";
import * as pageStyles from "../users/UsersListPage.css";
import { MigrationPreviewModal } from "./MigrationPreviewModal";
import { downloadCertificatePdf } from "../../lib/certificate/certificatePdf";
import { useCertificationContentDefinitions } from "../../lib/i18n/useCertificationContentDefinitions";
import { useToast } from "../../../ui/components/Toast";

function formatClaimedBy(claim: ProgressClaim): string {
  if (!claim.claimedBy) return "—";
  const name = [claim.claimedBy.firstName, claim.claimedBy.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  return name || claim.claimedBy.email;
}

function formatWhen(iso: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function claimStatusKey(claim: ProgressClaim): "pending" | "claimed" {
  return claim.claimedAt ? "claimed" : "pending";
}

function StatusCell({ claimed }: { claimed: boolean }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "2px 8px",
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 600,
        background: vars.color.muted,
        color: claimed ? vars.color.success : vars.color.warningFg,
        border: `1px solid ${vars.color.border}`,
      }}
    >
      {claimed ? "Claimed" : "Pending"}
    </span>
  );
}

function asStringArray(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map(String).filter(Boolean);
  }
  if (typeof value === "string" && value) return [value];
  return [];
}

export function CertificationProgressClaimsPage() {
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const { t, currentLanguage } = useI18n();
  const { resolve } = useCertificationContentDefinitions();
  const toast = useToast();
  const token = accessToken ?? getAuthCookie("access_token");
  const [previewClaimId, setPreviewClaimId] = React.useState<string | null>(
    null,
  );
  const [previewOpen, setPreviewOpen] = React.useState(false);
  const [downloadingClaimId, setDownloadingClaimId] = React.useState<
    string | null
  >(null);

  const claimsQuery = useQuery({
    ...certificationProgressClaimsQueryOptions(token ?? ""),
    enabled: Boolean(token),
  });

  const claims = claimsQuery.data ?? [];
  const loading = claimsQuery.isLoading;
  const error = claimsQuery.error
    ? (claimsQuery.error as Error).message ||
      t("pro.system.certification.error.loadClaims")
    : null;

  const pendingCount = claims.filter((c) => !c.claimedAt).length;
  const claimedCount = claims.length - pendingCount;

  const fetchList = React.useCallback(
    (params: ListParams): Promise<ListResult<ProgressClaim>> => {
      const start = (params.page - 1) * params.limit;
      const q = params.search?.toLowerCase().trim();
      const statusFilter = asStringArray(params.filters?.status);
      let filtered = claims;
      if (statusFilter.length > 0) {
        filtered = filtered.filter((c) =>
          statusFilter.includes(claimStatusKey(c)),
        );
      }
      if (q) {
        filtered = filtered.filter((c) => {
          const claimedName = formatClaimedBy(c).toLowerCase();
          return (
            c.email.toLowerCase().includes(q) ||
            (c.legacyUserId ?? "").toLowerCase().includes(q) ||
            claimedName.includes(q) ||
            c.moduleSlugs.some((s) => s.toLowerCase().includes(q))
          );
        });
      }
      return Promise.resolve({
        data: filtered.slice(start, start + params.limit),
        total: filtered.length,
      });
    },
    [claims],
  );

  const openPreview = React.useCallback((claimId: string) => {
    setPreviewClaimId(claimId);
    setPreviewOpen(true);
  }, []);

  const downloadClaimCertificates = React.useCallback(
    async (claim: ProgressClaim) => {
      if (!token) return;
      setDownloadingClaimId(claim.id);
      try {
        const [preview, catalog] = await Promise.all([
          previewProgressClaimMigration(token, claim.id),
          fetchCertificationModules(token),
        ]);
        const completed = preview.modules.filter((m) => m.completedAt);
        if (completed.length === 0) {
          toast.warning(
            t("pro.system.certification.claims.noCompletedModules"),
          );
          return;
        }
        const catalogById = new Map(catalog.map((m) => [m.id, m]));
        const recipientName = preview.existingUser
          ? [preview.existingUser.firstName, preview.existingUser.lastName]
              .filter(Boolean)
              .join(" ")
              .trim() || preview.existingUser.email
          : claim.email;
        for (const module of completed) {
          const catalogModule = module.mappedModuleId
            ? catalogById.get(module.mappedModuleId)
            : undefined;
          const resolvedCourseTitle =
            resolve(module.mappedModuleTitle ?? module.moduleSlug).trim()
            || module.mappedModuleTitle
            || module.moduleSlug;
          const headerTextOverride = catalogModule?.certificateHeaderText
            ? resolve(catalogModule.certificateHeaderText).trim()
            : "";
          const courseTextOverride = catalogModule?.certificateCourseText
            ? resolve(catalogModule.certificateCourseText).trim()
            : "";
          downloadCertificatePdf({
            recipientName,
            courseTitle: courseTextOverride || resolvedCourseTitle,
            completedAt: module.completedAt,
            certificateNumber: module.certNum,
            labels: {
              heading: headerTextOverride || t("certification.certificate.heading"),
              presentedTo: t("certification.certificate.presentedTo"),
              completionLine: t("certification.certificate.completionLine"),
              completedOn: t("certification.quiz.completedOn"),
              certificateNumber: t("certification.certificate.numberLabel"),
              issuedBy: t("certification.certificate.issuedBy"),
            },
            locale: currentLanguage?.code,
          });
        }
      } catch (e) {
        toast.error(
          t("certification.certificate.downloadFailed"),
          e instanceof Error ? e.message : undefined,
        );
      } finally {
        setDownloadingClaimId(null);
      }
    },
    [token, toast, t, currentLanguage?.code, resolve],
  );

  const columns = React.useMemo<ColumnDefinition<ProgressClaim>[]>(
    () => [
      {
        id: "status",
        label: t("pro.system.certification.col.status"),
        filterKey: "status",
        filterLabel: t("pro.system.certification.col.status"),
        showFilterByDefault: true,
        filterType: "checkbox",
        filterOptions: () => [
          { value: "pending", label: "Pending" },
          { value: "claimed", label: "Claimed" },
        ],
        filterAccessor: (row) => claimStatusKey(row),
        render: (row) => <StatusCell claimed={Boolean(row.claimedAt)} />,
      },
      {
        id: "email",
        label: t("pro.system.certification.col.email"),
        render: (row) => <span style={{ fontWeight: 600 }}>{row.email}</span>,
      },
      {
        id: "modules",
        label: t("pro.system.certification.claims.col.modules"),
        render: (row) => (
          <span title={row.moduleSlugs.join(", ")}>
            {row.moduleCount}/{row.trainingCount}{" "}
            <span style={{ color: vars.color.fgMuted, fontSize: 12 }}>
              {row.moduleSlugs.join(", ") || "—"}
            </span>
          </span>
        ),
      },
      {
        id: "createdAt",
        label: t("pro.system.certification.claims.col.imported"),
        render: (row) => formatWhen(row.createdAt),
      },
      {
        id: "claimedAt",
        label: t("pro.system.certification.claims.col.claimedAt"),
        render: (row) => formatWhen(row.claimedAt),
      },
      {
        id: "claimedBy",
        label: t("pro.system.certification.claims.col.claimedBy"),
        render: (row) => {
          if (!row.claimedByUserId) return <span>—</span>;
          return (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                navigate({
                  to: "/workspace/users/$userId",
                  params: { userId: row.claimedByUserId! },
                });
              }}
              style={{
                background: "none",
                border: "none",
                padding: 0,
                color: vars.color.accent,
                cursor: "pointer",
                font: "inherit",
                textAlign: "left",
              }}
            >
              {formatClaimedBy(row)}
            </button>
          );
        },
      },
    ],
    [navigate, t],
  );

  if (!token) {
    return (
      <div className={pageStyles.page}>
        <Callout variant="error">{t("pro.users.signInRequired")}</Callout>
      </div>
    );
  }

  return (
    <DetailPageLayout
      backLink={{
        label: t("pro.system.certification.title"),
        onClick: () => navigate({ to: "/system/certifications" }),
      }}
      header={{
        media: <ArchiveRestore size={48} />,
        title: t("pro.system.certification.claims.title"),
        subtitle: t("pro.system.certification.claims.subtitle"),
      }}
    >
      <div className={pageStyles.tableHost}>
        {error && (
          <div style={{ marginBottom: 16 }}>
            <Callout variant="error">{error}</Callout>
          </div>
        )}
        {!loading && claims.length > 0 && (
          <Text
            level="body-small"
            style={{
              color: vars.color.fgMuted,
              marginBottom: 12,
              display: "block",
            }}
          >
            {t("pro.system.certification.claims.summary", {
              pending: String(pendingCount),
              claimed: String(claimedCount),
              total: String(claims.length),
            })}
          </Text>
        )}
        {loading && claims.length === 0 ? (
          <Text level="body-small" style={{ color: vars.color.fgMuted }}>
            {t("shared.common.loading")}
          </Text>
        ) : (
          <ListView<ProgressClaim>
            listId="certification-progress-claims"
            contextKey={`${claims.length}:${pendingCount}:${claimedCount}`}
            queryOptions={{ staleTime: Infinity }}
            columns={columns}
            fetchList={fetchList}
            enabledViews={["table", "stack"]}
            compact
            disableLocalPersistence
            defaultPageSize={25}
            enableSelection
            searchPlaceholder={t("pro.system.certification.claims.search")}
            emptyTitle={t("pro.system.certification.claims.emptyTitle")}
            emptySubtitle={t("pro.system.certification.claims.emptySubtitle")}
            getRowLabel={(row) => row.email}
            getContextActions={(row) => [
              {
                id: "preview-migration",
                label: t("pro.system.certification.claims.previewAction"),
                bulk: false,
                onClick: () => openPreview(row.id),
              },
              {
                id: "download-certificate",
                label: t(
                  "pro.system.certification.claims.downloadCertificateAction",
                ),
                bulk: false,
                disabled: downloadingClaimId === row.id,
                onClick: () => void downloadClaimCertificates(row),
              },
            ]}
            renderStackCard={(row, ctx) => {
              const claimed = Boolean(row.claimedAt);
              const localPart = row.email.split("@")[0] ?? row.email;
              return (
                <ListViewCard
                  id={row.id}
                  layout="stack"
                  compact={ctx.compact}
                  imageFormat="avatar"
                  image={
                    <Avatar
                      email={row.email}
                      firstName={localPart}
                      lastName=""
                      size={72}
                    />
                  }
                  title={row.email}
                  subtitle={
                    row.moduleSlugs.length
                      ? `${row.moduleSlugs.join(", ")} · ${row.moduleCount} modules / ${row.trainingCount} trainings`
                      : `${row.moduleCount} modules / ${row.trainingCount} trainings`
                  }
                  badges={[
                    claimed
                      ? { id: "status", label: "Claimed", variant: "active" }
                      : {
                          id: "status",
                          label: "Pending",
                          variant: "pending",
                        },
                  ]}
                  footerText={
                    claimed
                      ? `Claimed ${formatWhen(row.claimedAt)} by ${formatClaimedBy(row)}`
                      : `Imported ${formatWhen(row.createdAt)} · awaiting signup`
                  }
                  onClick={ctx.onClick}
                />
              );
            }}
            onRowClick={(row) => {
              if (row.claimedByUserId) {
                navigate({
                  to: "/workspace/users/$userId",
                  params: { userId: row.claimedByUserId },
                });
              }
            }}
          />
        )}
      </div>

      <MigrationPreviewModal
        open={previewOpen}
        setOpen={setPreviewOpen}
        claimId={previewClaimId}
        accessToken={token}
      />
    </DetailPageLayout>
  );
}
